import { useQuery } from '@tanstack/react-query';
import {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridRowParams,
    GridSortModel,
} from '@mui/x-data-grid';

import {
    DataGrid,
    DataGridCard,
    dataGridNestedObjectValueGetter,
} from '@barboxx/admin-components';
import {
    ACTIVE_WIDTH,
    DEFAULT_WIDTH,
    DISPLAY_ORDER_WIDTH,
    NAME_MIN_WIDTH,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessCategory, Category } from '@barboxx/models';

type Columns = GridColDef<BusinessCategory.DetailsResponse>[];
const COLUMNS: Columns = [
    {
        field: 'displayName',
        headerName: 'Display Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
    },
    {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: DISPLAY_ORDER_WIDTH,
        type: 'number',
    },
    {
        field: 'isActive',
        headerName: 'Active',
        width: ACTIVE_WIDTH,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'featuredCategory',
        headerName: 'Featured',
        width: DEFAULT_WIDTH,
        type: 'boolean',
        align: 'center',
        headerAlign: 'center',
    },
];

interface Props {
    data: BusinessCategory[];
    size: number;
    page: number;
    total: number;
    onRowClick: (id: string) => void;
    onChangeSort: (sort: GridSortModel) => void;
    handleChangePageModel: (
        model: GridPaginationModel,
        details: GridCallbackDetails
    ) => void;
}

export function BusinessCategoriesTable(props: Props) {
    const {
        data = [],
        total,
        size,
        page,
        onRowClick,
        handleChangePageModel,
        onChangeSort,
    } = props;

    const { businessList } = useBusinessSelector();

    const categoriesQuery = useQuery({
        queryKey: ['categories'],
        queryFn: () => Api.Categories.Category.getCategories(),
    });

    const handleRowClick = (param: GridRowParams) => {
        onRowClick(param.id as string);
    };

    const columns: Columns = [
        {
            field: 'businessId',
            headerName: 'Business',
            width: NAME_MIN_WIDTH,
            type: 'singleSelect',
            valueOptions(params) {
                return (
                    businessList.map(business => ({
                        value: business.id,
                        label: business.place.name,
                    })) ?? []
                );
            },
            flex: 2,
        },
        {
            field: 'categoryId',
            headerName: 'Category',
            editable: true,
            width: DEFAULT_WIDTH,
            valueGetter: dataGridNestedObjectValueGetter,
            type: 'singleSelect',
            flex: 1,
            valueOptions(params) {
                return (
                    categoriesQuery.data?.data?.results.map(
                        (category: Category) => ({
                            value: category.id,
                            label: category.name,
                        })
                    ) ?? []
                );
            },
        },
        {
            field: 'categoryType',
            headerName: 'Category Type',
            editable: true,
            width: DEFAULT_WIDTH,
            valueGetter: (value, row) => row.categoryId,
            type: 'singleSelect',
            flex: 1,
            sortable: false,
            valueOptions(params) {
                return (
                    categoriesQuery.data?.data?.results.map(
                        (category: Category) => ({
                            value: category.id,
                            label: category.categoryType,
                        })
                    ) ?? []
                );
            },
        },
        ...COLUMNS,
        // {
        //     field: 'actions',
        //     type: 'actions',
        //     headerName: 'Actions',
        //     width: ACTIONS_WIDTH,
        //     cellClassName: 'actions',
        //     display: 'flex',
        //     align: 'right',
        //     getActions: (params: GridRowParams<BusinessCategory>) => [
        //         <EditSaveAction key="editSave" {...params} />,
        //         <CancelRelateAction
        //             key="cancelRelate"
        //             {...params}
        //             onDelete={onDelete}
        //         />,
        //     ],
        // },
    ];

    return (
        <DataGridCard title="Categories">
            <DataGrid
                rows={data}
                columns={columns}
                paginationModel={{
                    page,
                    pageSize: size,
                }}
                onPaginationModelChange={handleChangePageModel}
                rowCount={total}
                disableColumnMenu
                onRowClick={handleRowClick}
                onSortModelChange={onChangeSort}
            />
        </DataGridCard>
    );
}
